const { createApp, ref, component, watch, onMounted, reactive, onBeforeMount, onUnmounted } = Vue;

const starterKitWidget = createApp({
	name: "starterKitWidget",
	setup() {
		const starterKitDataRef = ref(starterkitData);
		const activeStep = ref("STARTER_KIT");
		const selectedProductVariant = ref(null);
		const selectedAddonVariant = ref(null);
		const dropdownOpen = ref(false);
		const addOnQty = ref(1);
		const eventsAttachedToAtc = ref(false);
		const isSubmitting = ref(false);
		const showMemberPrice = ref(false);

		onBeforeMount(() => {
			const urlParams = new URLSearchParams(window.location.search);
			const variantId = urlParams.get("variant");

			starterKitDataRef.value.addons = starterkitData.addons.map((p) => {
				return {
					...p,
					price_info: calculateDiscount(p, null),
				};
			});

			let firstAvailableProduct = null;

			if (variantId) {
				firstAvailableProduct = starterkitData.main_product.variants.find(
					(v) => String(v.id) === variantId && v.hidden != true
				);
			}

			if (!firstAvailableProduct) {
				firstAvailableProduct = starterkitData.main_product.variants.find((v) => v.hidden != true);
			}

			selectedProductVariant.value = firstAvailableProduct;

			const firstAvailableAddon = starterKitDataRef.value.addons.find((p) => p.available && p.hidden != true);
			// console.log(firstAvailableAddon, "first available addon", starterKitDataRef.value.addons);
			if (firstAvailableAddon) {
				selectedAddonVariant.value = firstAvailableAddon;
			}

			preselectProductVariantQueryParam();

			calculateRevisedPrice();
			reHookEventsOnAtcBtn();
			window.addEventListener("membershipOptionChanged", handleMemberOptionChange);
		});

		onMounted(() => {
			const carouselEl = document.querySelector("media-carousel");
			if (carouselEl) {
				setTimeout(() => {
					updateMedia(selectedProductVariant.value);
				}, 2000);
			}
		});

		onUnmounted(() => {
			window.removeEventListener("membershipOptionChanged", handleMemberOptionChange);
		});

		watch(activeStep, () => {
			calculateRevisedPrice();
			updateMedia(selectedProductVariant.value);
		});

		watch(isSubmitting, () => handleATCLoading());

		function handleMemberOptionChange(e) {
			// This is added to prevent the race conditions so DO NOT REMOVE THIS!!
			setTimeout(() => {
				calculateRevisedPrice();
			}, 80);
		}

		function handleAddonSelect(e) {
			selectedAddonVariant.value = starterkitData.addons.find((p) => p.id == e.target.value);
			dropdownOpen.value = false;
			calculateRevisedPrice();
		}

		function handleMainProductSelect(e) {
			selectedProductVariant.value = starterkitData.main_product.variants.find((v) => v.id == e.target.value);
			if (selectedProductVariant.value) {
				dispatchVariantChangedEvent(selectedProductVariant.value);
				updateVariantInURL(selectedProductVariant.value.id);
				updateMedia(selectedProductVariant.value);
				calculateRevisedPrice();
			}
		}

		function handleVariantChange(e) {
			selectedProductVariant.value = e;
			if (selectedProductVariant.value) {
				dispatchVariantChangedEvent(selectedProductVariant.value);
				updateVariantInURL(selectedProductVariant.value.id);
				updateMedia(selectedProductVariant.value);
				calculateRevisedPrice();
				handleAtcOOS();
			}
		}

		function handleAtcOOS() {
			const atcBtn = document.querySelector("buy-buttons .button--add-to-cart");
			const actBtnText = atcBtn?.querySelector("div");
			const stickyATCs = document.querySelectorAll(".product-quick-add buy-buttons #AddToCart");
			if (selectedProductVariant.value.available === false) {
				if (!atcBtn || !stickyATCs) return;
				atcBtn.setAttribute("disabled", true);
				atcBtn.classList.add("button--subdued");
				actBtnText.textContent = starterkitData.sold_out_label;
				stickyATCs.forEach((b) => {
					b.setAttribute("disabled", true);
					b.classList.add("button--subdued");
					b.textContent = starterkitData.sold_out_label;
				});
			} else {
				if (!atcBtn || !stickyATCs) return;
				atcBtn.removeAttribute("disabled");
				atcBtn.classList.remove("button--subdued");
				actBtnText.textContent = starterkitData.add_to_label;
				stickyATCs.forEach((b) => {
					b.removeAttribute("disabled");
					b.classList.remove("button--subdued");
					b.textContent = starterkitData.add_to_label;
				});
			}
		}

		function dispatchVariantChangedEvent(variant) {
			document.dispatchEvent(
				new CustomEvent("variant:change", {
					bubbles: true,
					detail: { variant },
				})
			);
		}

		function updateVariantInURL(variantId) {
			const url = new URL(window.location.href);
			url.searchParams.set("variant", variantId);
			window.history.replaceState({}, "", url);
		}

		async function updateMedia(variant) {
			const carouselEl = document.querySelector("media-carousel");
			let featuredMediaPosition = null;
			let selectedVariantId = variant?.id;
			let stepsTypeObj = starterKitDataRef.value.steps_type.find((s) => s.type === activeStep.value) || null;

			if (activeStep.value === "STARTER_KIT" && stepsTypeObj && Array.isArray(stepsTypeObj.variant_image_map)) {
				let imageMapping = stepsTypeObj.variant_image_map.find((m) => m?.variant_id == selectedVariantId);

				if (imageMapping === undefined && stepsTypeObj.length) {
					imageMapping = stepsTypeObj[0].image_index - 1;
				}
				featuredMediaPosition = imageMapping.image_index - 1;
			} else if (activeStep.value === "ADDON_ONLY" && stepsTypeObj && Array.isArray(stepsTypeObj.variant_image_map)) {
				featuredMediaPosition = stepsTypeObj.variant_image_map[0]?.image_index - 1;
			} else {
				featuredMediaPosition = variant?.featured_media?.position ? variant.featured_media.position - 1 : null;
			}

			if (carouselEl && carouselEl?.select) {
				await carouselEl.select(featuredMediaPosition);
			}
		}

		function calculateRevisedPrice() {
			setShowMemberprice();
			let salePrice = 0;
			let discountPrice = null;
			let savings = null;
			let memberShipPriceEnabled = showMemberPrice.value;
			if (activeStep.value === "STARTER_KIT") {
				salePrice = parseFloat(window.mainProductPrice.sale_price) || 0;
				salePrice = salePrice + selectedAddonVariant.value?.price_info?.price;
				discountPrice = calculateDiscountedPrice(salePrice, parseFloat(starterkitData.additional_discount) || null);
				if (memberShipPriceEnabled) {
					discountPrice = calculateDiscountedPrice(
						salePrice,
						parseFloat(starterkitData.membership_additional_discount) || null
					);
				}
				let totalDiscount = 0;

				if (discountPrice) {
					totalDiscount = getDiscountPercentage(salePrice, discountPrice);
				}

				if (totalDiscount) {
					savings = [`SAVE ${totalDiscount.toFixed()}%`];
					// console.log(savings, "save label");
				}
			} else if (activeStep.value === "DIFFUSER_ONLY") {
				salePrice = parseFloat(window.mainProductPrice.sale_price) || 0;
				if (window.mainProductPrice?.discount_price) {
					discountPrice = window.mainProductPrice.discount_price
						? parseFloat(window.mainProductPrice.discount_price)
						: null;
					savings = [`SAVE ${getDiscountPercentage(salePrice, discountPrice).toFixed()}%`];
				}
			} else if (activeStep.value === "ADDON_ONLY") {
				salePrice = selectedAddonVariant.value?.price_info?.price;
				discountPrice = null;

				if (selectedAddonVariant.value?.price_info.discount_price) {
					discountPrice = selectedAddonVariant.value?.price_info.discount_price;
					savings = [`SAVE ${getDiscountPercentage(salePrice, discountPrice).toFixed()}%`];
				}

				if (selectedAddonVariant.value?.price_info.member_price && memberShipPriceEnabled) {
					discountPrice = selectedAddonVariant.value?.price_info.member_price;
					savings = [`SAVE ${getDiscountPercentage(salePrice, discountPrice).toFixed()}%`];
				}
			}

			updateMainProductLinePrice({ salePrice, discountPrice, savings }, activeStep.value);
		}

		function setShowMemberprice() {
			let showMemberPriceState = false;
			let isEligibleMember = window?.membershipSettings?.isActiveMember || false;

			if (isEligibleMember) {
				showMemberPriceState = true;
			} else if (window.IS_ELIGIBLE_MEMBER_SR) {
				showMemberPriceState = true;
			} else {
				showMemberPriceState = false;
			}

			showMemberPrice.value = showMemberPriceState;
		}

		function reHookEventsOnAtcBtn() {
			try {
				const mutationHandler = function (mutationsList, observer) {
					for (let mutation of mutationsList) {
						if (mutation.type === "childList" && mutation.addedNodes.length > 0) {
							let atcBtn = document.querySelector("buy-buttons .button--add-to-cart");
							// Check if the "AddToCart" button is now in the DOM
							if (atcBtn === mutation.target && atcBtn.querySelector("div")) {
								observer.disconnect();
								//console.log('mutation running');
								let clonedAtcBtn = atcBtn.cloneNode(true);
								clonedAtcBtn.setAttribute("type", "button");
								atcBtn.parentNode.replaceChild(clonedAtcBtn, atcBtn);
								clonedAtcBtn.addEventListener("click", handleAddToCart);
								reHookEventsOnQtyBtns();
								eventsAttachedToAtc.value = true;
								const stickyATC = document.querySelectorAll(".product-quick-add buy-buttons #AddToCart");
								if (stickyATC && stickyATC.length) {
									stickyATC.forEach((b) => {
										if (b) {
											let clonedStickyAtc = b.cloneNode(true);
											clonedStickyAtc.setAttribute("type", "button");
											b.parentNode.replaceChild(clonedStickyAtc, b);
											clonedStickyAtc.addEventListener("click", handleAddToCart);
										}
									});
								}
								handleAtcOOS();
							}
						}
					}
				};
				const observer = new MutationObserver(mutationHandler);
				observer.observe(document.body, { childList: true, subtree: true });
			} catch (error) {
				console.log(error);
			}
		}

		function reHookEventsOnQtyBtns() {
			let qtySelector = document.querySelector("quantity-selector.quantity-selector");
			if (qtySelector) {
				let qtySelectorBtns = qtySelector.querySelectorAll("button.quantity-selector__button");
				qtySelectorBtns[0].addEventListener("click", () => handleQtySelect("-"));
				qtySelectorBtns[1].addEventListener("click", () => handleQtySelect("+"));
				document.querySelector("quantity-selector input.quantity-selector__input").style["pointer-events"] = "none";
			}
		}

		function handleQtySelect(action) {
			let qtySelectorInputEl = document.querySelector(`quantity-selector.quantity-selector input[name="quantity"]`);
			addOnQty.value = parseInt(qtySelectorInputEl.value || 1);
		}

		function handleATCLoading() {
			let atcBtn = document.querySelector("buy-buttons .button--add-to-cart");
			const stickyATCs = document.querySelectorAll(".product-quick-add buy-buttons #AddToCart");
			if (isSubmitting.value) {
				atcBtn.setAttribute("aria-busy", true);
				atcBtn.setAttribute("disabled", true);
				stickyATCs.forEach((b) => {
					if (b) {
						b.setAttribute("aria-busy", true);
						b.setAttribute("disabled", true);
					}
				});
			} else {
				atcBtn.removeAttribute("aria-busy");
				atcBtn.removeAttribute("disabled");
				stickyATCs.forEach((b) => {
					if (b) {
						b.removeAttribute("aria-busy");
						b.removeAttribute("disabled");
					}
				});
			}
		}

		async function handleAddToCart() {
			const checkedOption = document.querySelector('input[name="firstclass-membership"]:checked');
			const compare_at_price_property = document.getElementById("line-item-custom-property");
			const compare_at_price_percentage_property = document.getElementById("line-item-custom-property-percentage");
			const isProductEligible = !!$('input[name="properties[__membership_eligible]"]').val();

			let commonProperties = {};
			let bundleGroupId = [selectedProductVariant.value.id, selectedAddonVariant.value.id].join("_");
			let bundleInstanceId = `_bundle_instance_${Date.now()}_${Math.random().toString(36).slice(2)}`;
			let bundleId = `${bundleGroupId}_${bundleInstanceId}`;
			let payload = [];

			// console.log("handleAddToCart", activeStep.value, bundleId);

			if (activeStep.value === "STARTER_KIT" && bundleId) {
				commonProperties["_bundle_id"] = bundleId;
				commonProperties["_bundle_group_id"] = bundleGroupId;
				commonProperties["_bundle_instance_id"] = bundleInstanceId;
				if (starterkitData.starterkit_thumbnail) {
					commonProperties["_bundle_image_url"] = starterkitData.starterkit_thumbnail;
				}
				commonProperties["_starter_kit_bundle"] = "true";
			}

			if (activeStep.value === "STARTER_KIT" || activeStep.value === "DIFFUSER_ONLY") {
				let membershipEligibleprop = {};
				if (isProductEligible) {
					membershipEligibleprop["__membership_eligible"] = "true";
				}
				payload.push({
					id: selectedProductVariant.value.id,
					quantity: addOnQty.value,
					properties: {
						...commonProperties,
						_compare_at_price: compare_at_price_property.value,
						_compare_at_price_discount: compare_at_price_percentage_property.value,
						...membershipEligibleprop,
					},
				});
			}

			if (activeStep.value === "STARTER_KIT" || activeStep.value === "ADDON_ONLY") {
				if (activeStep.value === "STARTER_KIT" && bundleId) {
					commonProperties["_bundle_identifier"] = "true";
				}
				let membershipEligibleprop = {};
				if (selectedAddonVariant.value.membership_discount) {
					membershipEligibleprop["__membership_eligible"] = "true";
				}
				payload.push({
					id: selectedAddonVariant.value.id,
					quantity: addOnQty.value,
					properties: {
						...commonProperties,
						...membershipEligibleprop,
					},
				});
			}
			try {
				window.setATCErrorMessage("");
				isSubmitting.value = true;

				if ($("#membershipSubsWidget").hasClass("active") && checkedOption) {
					if (checkedOption.value === "MEMBERSHIP") {
						writeCookie("_is_eligible_member", "true", 10);
					} else {
						writeCookie("_is_eligible_member", "false", 10);
					}
				}

				const response = await fetch(window.Shopify.routes.root + "cart/add.js", {
					method: "POST",
					headers: {
						"Content-Type": "application/json",
					},
					body: JSON.stringify({ items: payload }),
				});
				const data = await response.json();
				if (response.status !== 200) {
					if (data.message) {
						return window.setATCErrorMessage(data.message);
					}
				}
			} catch (error) {
				console.log(error);
				window.setATCErrorMessage("Oops! Something went wrong while adding products to the cart.");
			} finally {
				isSubmitting.value = false;
			}
		}

		function preselectProductVariantQueryParam() {
			const urlParams = new URLSearchParams(window.location.search);
			const variantId = urlParams.get("variant");
			if (variantId) {
				selectedProductVariant.value = starterkitData.main_product.variants.find((v) => v.id == variantId);
			}
		}

		return {
			activeStep,
			starterKitDataRef,
			selectedProductVariant,
			selectedAddonVariant,
			dropdownOpen,
			eventsAttachedToAtc,
			showMemberPrice,
			formatMoneyy,
			imgUrl,
			handleAddonSelect,
			handleMainProductSelect,
			handleVariantChange,
			preselectProductVariantQueryParam,
		};
	},
	delimiters: ["${", "}"],
});

starterKitWidget.component("product-variant-select", {
	template: "#variantSelect",
	props: ["selected_product", "main_product"],
	emits: ["on_variant_change"],
	setup(props, ctx) {
		const selectedVariant = ref(null);
		const variantContEl = ref(null);
		const productObj = ref(null);

		onBeforeMount(() => {
			productObj.value = props.main_product;
		});

		onMounted(() => {
			selectedVariant.value = props.selected_product;
			if (selectedVariant.value) {
				handleVariantChecked();
				hideUnRelatedVariants();
				ctx.emit("on_variant_change", selectedVariant.value);
			}
		});

		function handleVariantChecked() {
			if (variantContEl.value) {
				selectedVariant.value.options.forEach((option_value) => {
					variantContEl.value.querySelectorAll(`input[type='radio'][value='${option_value}']`).forEach((el) => {
						el.checked = true;
					});
				});
			}
		}

		function hideUnRelatedVariants() {
			const optionWithValues = productObj.value?.options_with_values;
			const activeOptions = [...selectedVariant.value.options];
			const productVariants = productObj.value.variants;
			const swatchGroups = variantContEl.value.querySelectorAll(".variant-block__swatch");
			variantContEl.value
				.querySelectorAll(`input[type='radio']`)
				.forEach((el) => el.parentElement.classList.remove("hide-option"));
			swatchGroups.forEach((grpEl, i) => {
				let groupPosition = parseInt(grpEl.getAttribute("data-option-position"));
				if (optionWithValues.length > 1 && groupPosition > 1 && groupPosition <= optionWithValues.length) {
					const primaryOptions = activeOptions.slice(0, i);
					let groupInputs = grpEl.querySelectorAll('input[type="radio"]');
					if (groupInputs) {
						groupInputs.forEach((el) => {
							let isPartOfAnyVariant = false;
							for (let variant of productVariants) {
								const optionCombination = [...primaryOptions, el.value];
								const isUnRelatedVariant = optionCombination.every((o) => variant.options.includes(o));
								if (isUnRelatedVariant) {
									isPartOfAnyVariant = true;
									break;
								}
							}
							if (!isPartOfAnyVariant) {
								el.parentElement.classList.add("hide-option");
							}
						});
					}
				}
			});
		}

		function handleDisableVariant(option_name) {
			return selectedVariant.value.options.includes(option_name);
		}

		function handleVariantSelect(e) {
			const selectedOptionArr = [];
			const selectedVariantInputRadio = variantContEl.value.querySelectorAll("input[type='radio']:checked");
			const productVariants = productObj.value.variants;
			let matchedVariant = null;
			selectedVariantInputRadio.forEach((el) => {
				selectedOptionArr.push(el.value);
			});
			if (selectedOptionArr.length) {
				for (let variant of productVariants) {
					const isSelectedVariant = selectedOptionArr.every((o) => variant.options.includes(o));
					if (isSelectedVariant) {
						matchedVariant = variant;
						break;
					}
				}
				if (matchedVariant) {
					selectedVariant.value = matchedVariant;
				} else {
					let partialCombination = [e.target.value];
					if (selectedOptionArr.length > 2) {
						const optionsLength = productObj.value?.options_with_values.length;
						partialCombination = selectedOptionArr.slice(0, optionsLength - 1);
					}
					for (let variant of productVariants) {
						const isSelectedVariant = partialCombination.every((o) => variant.options.includes(o));
						if (isSelectedVariant) {
							matchedVariant = variant;
							break;
						}
					}
					if (!matchedVariant) {
						matchedVariant = productVariants.find((variant) => variant.options.includes(e.target.value));
					}
				}
			}
			selectedVariant.value = matchedVariant;
			handleVariantChecked();
			hideUnRelatedVariants();
			ctx.emit("on_variant_change", selectedVariant.value);
		}

		return {
			selectedVariant,
			variantContEl,
			productObj,
			handleVariantChecked,
			handleVariantSelect,
			handleize,
			handleDisableVariant,
		};
	},
	delimiters: ["${", "}"],
});

starterKitWidget.mount("#starterKitWidget");

function imgUrl(url, size) {
	if (!url)
		url = `https://cdn.shopify.com/shopifycloud/shopify/assets/no-image-2048-5e88c1b20e087fb7bbe9a3771824e743c244f437e4f8ba93bbf7b11b53f7824c.gif`;
	// remove any current image size then add the new image size
	return url
		.replace(/_(pico|icon|thumb|small|compact|medium|large|grande|original|1024x1024|2048x2048|master)+\./g, ".")
		.replace(/\.jpg|\.png|\.gif|\.jpeg/g, function (match) {
			return "_" + size + match;
		});
}

function formatMoneyy(cents, format = "") {
	if (typeof cents === "string") {
		cents = cents.replace(".", "");
	}
	const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/,
		formatString = format || window.themeVariables.settings.moneyFormat;
	function defaultTo(value2, defaultValue) {
		return value2 == null || value2 !== value2 ? defaultValue : value2;
	}
	function formatWithDelimiterss(number, precision, thousands, decimal) {
		precision = defaultTo(precision, 2);
		thousands = defaultTo(thousands, ",");
		decimal = defaultTo(decimal, ".");
		if (isNaN(number) || number == null) {
			return 0;
		}
		number = roundNumber(number / 100, false, precision);
		let parts = number.split("."),
			dollarsAmount = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, "$1" + thousands),
			centsAmount = parts[1] ? decimal + parts[1] : "";
		return dollarsAmount + centsAmount;
	}
	let value = "";
	switch (formatString.match(placeholderRegex)[1]) {
		case "amount":
			value = formatWithDelimiterss(cents, 2);
			break;
		case "amount_no_decimals":
			value = formatWithDelimiterss(cents, 0);
			break;
		case "amount_with_space_separator":
			value = formatWithDelimiterss(cents, 2, " ", ".");
			break;
		case "amount_with_comma_separator":
			value = formatWithDelimiterss(cents, 2, ".", ",");
			break;
		case "amount_with_apostrophe_separator":
			value = formatWithDelimiterss(cents, 2, "'", ".");
			break;
		case "amount_no_decimals_with_comma_separator":
			value = formatWithDelimiterss(cents, 0, ".", ",");
			break;
		case "amount_no_decimals_with_space_separator":
			value = formatWithDelimiterss(cents, 0, " ");
			break;
		case "amount_no_decimals_with_apostrophe_separator":
			value = formatWithDelimiterss(cents, 0, "'");
			break;
	}
	if (formatString.indexOf("with_comma_separator") !== -1) {
		return formatString.replace(placeholderRegex, value);
	} else {
		return formatString.replace(placeholderRegex, value);
	}
}

function roundNumber(num, savings, precision = 2) {
	if (isNaN(num)) {
		return;
	}

	let nummm = num.toFixed(3).split(".")[1].slice(2);
	if (nummm > 4 && !savings) {
		let number = Math.ceil((num + Number.EPSILON) * 100) / 100;
		return number.toFixed(precision);
	} else if (nummm < 6 && savings) {
		let number = Math.floor((num + Number.EPSILON) * 100) / 100;
		return number.toFixed(precision);
	} else {
		let number = Math.round((num + Number.EPSILON) * 100) / 100;
		return number.toFixed(precision);
	}
}

function calculateDiscountedPrice(salePrice = 0, discount = 0) {
	if (salePrice <= 0 || discount <= 0) {
		return null;
	}
	const discountAmount = (discount / 100) * salePrice;
	const discountedPrice = salePrice - discountAmount;

	return discountedPrice;
}

/**
 * Calculates the discounted prices for a product variant based on site-wide , collection and variant specific discounts.
 *
 * @param {Object} variant - The product variant object. *
 * @returns {Object} - The discounted prices and percentages for both one-time and subscription purchases.
 */
/**
 * Calculates the discounted prices for a product variant based on site-wide , collection and variant specific discounts.
 *
 * @param {Object} variant - The product variant object. *
 * @returns {Object} - The discounted prices and percentages for both one-time and subscription purchases.
 */
function calculateDiscount(variant) {
	let variantSubsDiscountValue = parseFloat(variant?.variant_discount_subs) || 0;
	let variantOnetimeDiscountValue = parseFloat(variant?.variant_discount_one_time) || 0;

	let doesCompareAtPriceExist = false;
	let variantSalePrice = variant.price;
	if (variant.compare_at_price > variant.price) {
		variantSalePrice = variant.compare_at_price;
		doesCompareAtPriceExist = true;
	}

	const productPriceObj = {
		price: variantSalePrice,
		SUBSCRIPTION: {
			discount_price: doesCompareAtPriceExist ? variant.price : 0,
			discount_percentage: doesCompareAtPriceExist
				? Math.round(((variant.compare_at_price - variant.price) / variant.compare_at_price) * 100)
				: 0,
			_debug_log: "",
		},
		ONE_TIME: {
			discount_price: doesCompareAtPriceExist ? variant.price : 0,
			discount_percentage: doesCompareAtPriceExist
				? Math.round(((variant.compare_at_price - variant.price) / variant.compare_at_price) * 100)
				: 0,
			_debug_log: "",
		},
	};

	if (doesCompareAtPriceExist) {
		if (!variantSubsDiscountValue) {
			variantSubsDiscountValue = getDiscountPercentage(variantSalePrice, variant.price);
		}
		if (!variantOnetimeDiscountValue) {
			variantOnetimeDiscountValue = getDiscountPercentage(variantSalePrice, variant.price);
		}
	}

	// variant level discounts gets the highest priority
	if (variantOnetimeDiscountValue) {
		productPriceObj.ONE_TIME.discount_price = calculateDiscountedPrice(variantSalePrice, variantOnetimeDiscountValue);
		productPriceObj.ONE_TIME.discount_percentage = variantOnetimeDiscountValue;
		productPriceObj.SUBSCRIPTION._debug_log = `Applying Variant discount ${variantOnetimeDiscountValue}`;
	}

	if (variantSubsDiscountValue) {
		productPriceObj.SUBSCRIPTION.discount_price = calculateDiscountedPrice(variantSalePrice, variantSubsDiscountValue);
		productPriceObj.SUBSCRIPTION.discount_percentage = variantSubsDiscountValue;
		productPriceObj.SUBSCRIPTION._debug_log = `Applying Variant discount ${variantSubsDiscountValue}`;
	}

	// membership price overide
	if (variant.membership_discount) {
		productPriceObj.member_price = calculateDiscountedPrice(variantSalePrice, variant.membership_discount);
	}

	return productPriceObj;
}

/**
 * Returns sum of the saleprice and discount price for the given addon array
 * @param {any[]} addOns
 * @param {boolean} enableMemberprice
 */
function getTotalAddonPrice(addOn, enableMemberprice = false) {
	let totalSalePrice = 0;
	let totalDiscountPrice = null;

	if (addOn.price_info) {
		if (enableMemberprice && addOn.price_info?.member_price) {
			totalSalePrice += addOn.price_info?.price;
			totalDiscountPrice += addOn.price_info?.member_price;
		} else {
			totalSalePrice += addOn.price_info.price;

			if (addOn.price_info["ONE_TIME"].discount_price) {
				totalDiscountPrice += addOn.price_info["ONE_TIME"].discount_price;
			}
		}
	}
	return { totalSalePrice, totalDiscountPrice };
}

function getTotalMemberNonMemberAddonPrice(addOn) {
	let addOnNonMemberPrice = 0;
	let addOnMemberPrice = 0;

	if (addOn.price_info) {
		const priceInfo = addOn.price_info;
		const activePurchaseOption = "ONE_TIME";
		if (addOn.price_info[activePurchaseOption].discount_price) {
			addOnNonMemberPrice += addOn.price_info[activePurchaseOption].discount_price;
		} else {
			addOnNonMemberPrice += priceInfo.price || 0;
		}

		if (priceInfo?.member_price) {
			addOnMemberPrice += priceInfo.member_price;
		} else {
			addOnMemberPrice += priceInfo.price || 0;
		}
	}

	return { addOnNonMemberPrice, addOnMemberPrice };
}

function getDiscountPercentage(originalPrice, salePrice) {
	if (!originalPrice || originalPrice <= 0) return 0;
	return ((originalPrice - salePrice) / originalPrice) * 100;
}

function updateMainProductLinePrice(headerBlock, productType = null) {
	const saveLabelsWrapEl = document.querySelector(".product-meta__label-list.prive-label-list");
	$("#prive_price_elem").html("");
	$(".sale_price").html("");
	$("#prive_price_elem").removeClass("membership");
	$(".sale_price").removeClass("membership");
	if (headerBlock.discountPrice != null) {
		$("#prive_price_elem").html(`<s>${formatMoneyy(headerBlock.salePrice)}</s>`);
		$(".sale_price").html(formatMoneyy(headerBlock.discountPrice)).css("color", "var(--brand-red-secondary)");
	} else {
		$("#prive_price_elem").html(formatMoneyy(headerBlock.salePrice));
	}

	if (window.mainProductPrice?.membership && window?.IS_ELIGIBLE_MEMBER_SR) {
		$(".sale_price").addClass("membership");
		$(".product-sticky-form__price").addClass("membership");
		if (productType !== "STARTER_KIT") {
			headerBlock.savings = [window.mainProductPrice?.membership.label];
		}
	}

	saveLabelsWrapEl.innerHTML = ``;
	if (saveLabelsWrapEl && headerBlock.savings && headerBlock.savings.length) {
		const savingsArr = Array.isArray(headerBlock.savings)
			? headerBlock.savings
			: [formatSavingsLabel(headerBlock.savings)];
		savingsArr.forEach((s, index) => {
			getDiscount = s;
			if (s) {
				// hardcoded rounding for 67% sale
				if (s === "SAVE 66.68%") {
					s = "SAVE 67.00%";
				}
				const saveLblsavingsLblEl = `
				<span class="case-${index + 1}">
					<span dir="ltr" class="label label--highlight product-thumbnail_badge case-${index + 1} ${
					window.mainProductPrice?.membership && window?.IS_ELIGIBLE_MEMBER_SR ? "label--membership" : ""
				}">
						<span>${s}</span>
					</span>
				</span>
				`;
				saveLabelsWrapEl.innerHTML += saveLblsavingsLblEl;
			}
		});
	}
}
function handleize(str, frm) {
	return str
		.toLowerCase()
		.replace(/\s+/g, "-")
		.replace(/[^\w\-]+/g, "")
		.replace(/\-\-+/g, "-")
		.replace(/^-+/, "")
		.replace(/-+$/, "");
}
